import axios from 'axios';
import {
  GET_ENTITY_CONFIG_URL,
  GET_FULL_CONFIG_BY_ENTITY_TYPE_URL,
  GET_FULL_ENTITY_CONFIG_URL,
  EXECUTE_ONBOARDING_URL,
  GET_ONBOARDING_STATUS_URL,
  LIST_ENTITY_CONFIGS_URL,
  PREPARE_ONBOARDING_URL,
  get_request_config,
} from '@/utils/system_constants/constant.js';

const request_cache = new Map();
const ALLOWED_CACHED_GET_URLS = new Set([
  LIST_ENTITY_CONFIGS_URL,
  GET_ENTITY_CONFIG_URL,
  GET_FULL_ENTITY_CONFIG_URL,
  GET_FULL_CONFIG_BY_ENTITY_TYPE_URL,
]);

function parse_json_string(value, fallback = {}) {
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function parse_response_data(response) {
  return parse_nested_response(response_payload(response));
}

function response_payload(response) {
  return response?.data ?? response ?? {};
}

function parse_nested_response(raw_data) {
  if (typeof raw_data !== 'string') return raw_data;

  return normalize_parsed_response(parse_json_string(raw_data));
}

function normalize_parsed_response(parsed_data) {
  return typeof parsed_data === 'string'
    ? parse_json_string(parsed_data)
    : parsed_data;
}

function to_array(value) {
  return Array.isArray(value) ? value : [];
}

function cache_key(name, params = {}) {
  const normalized_params = Object.entries(params)
    .sort(([left_key], [right_key]) => left_key.localeCompare(right_key))
    .map(([key, value]) => `${key}:${value ?? ''}`)
    .join('|');

  return `${name}|${normalized_params}`;
}

function assert_cached_get_url(url) {
  if (!ALLOWED_CACHED_GET_URLS.has(url)) {
    return;
  }
}

async function cached_get(name, url, params = {}) {
  assert_cached_get_url(url);
  const key = cache_key(name, params);

  if (request_cache.has(key)) {
    return request_cache.get(key);
  }

  const request = axios
    .get(url, get_request_config({ params }))
    .then((response) => parse_response_data(response)?.data);

  request_cache.set(key, request);

  try {
    return await request;
  } catch (error) {
    request_cache.delete(key);
    throw error;
  }
}

export function clearXVerdictEntityConfigCache() {
  request_cache.clear();
}

export async function listXVerdictEntityConfigs(params = {}) {
  const data = await cached_get('list_entity_configs', LIST_ENTITY_CONFIGS_URL, params);
  return to_array(data).filter((config) => config?.is_active !== false);
}

export async function getXVerdictEntityConfigById({ entity_config_id } = {}) {
  if (!entity_config_id) {
    return {};
  }

  return cached_get('get_entity_config', GET_ENTITY_CONFIG_URL, { entity_config_id });
}

export async function getXVerdictFullConfigById({ entity_config_id } = {}) {
  if (!entity_config_id) {
    return {};
  }

  return cached_get('get_full_entity_config', GET_FULL_ENTITY_CONFIG_URL, { entity_config_id });
}

export async function getXVerdictFullConfigByEntityType({
  entity_type_id,
  project_id = null,
  sub_entity_type_id = null,
} = {}) {
  if (!entity_type_id) {
    return {};
  }

  return cached_get(
    'get_full_config_by_entity_type',
    GET_FULL_CONFIG_BY_ENTITY_TYPE_URL,
    {
      entity_type_id,
      ...(project_id ? { project_id } : {}),
      ...(sub_entity_type_id ? { sub_entity_type_id } : {}),
    }
  );
}

export async function prepareXVerdictOnboarding({
  entity_type_id,
  project_id = null,
  sub_entity_type_id = null,
} = {}) {
  const response = await axios.get(
    PREPARE_ONBOARDING_URL,
    get_request_config({
      params: {
        entity_type_id,
        ...(project_id ? { project_id } : {}),
        ...(sub_entity_type_id ? { sub_entity_type_id } : {}),
      },
    })
  );
  return parse_response_data(response);
}

export async function executeXVerdictOnboarding({
  entity_type_id,
  entity_profile_id,
  application_id,
  project_id = null,
  sub_entity_type_id = null,
} = {}) {
  const response = await axios.post(
    EXECUTE_ONBOARDING_URL,
    {
      entity_type_id,
      entity_profile_id,
      application_id,
      ...(project_id ? { project_id } : {}),
      ...(sub_entity_type_id ? { sub_entity_type_id } : {}),
    },
    get_request_config()
  );
  return parse_response_data(response);
}

export async function getXVerdictOnboardingStatus({
  entity_type_id,
  entity_profile_id,
  application_id,
  project_id = null,
  sub_entity_type_id = null,
} = {}) {
  const response = await axios.get(
    GET_ONBOARDING_STATUS_URL,
    get_request_config({
      params: {
        entity_type_id,
        entity_profile_id,
        application_id,
        ...(project_id ? { project_id } : {}),
        ...(sub_entity_type_id ? { sub_entity_type_id } : {}),
      },
    })
  );
  return parse_response_data(response);
}
